import React from 'react';

import Tooltip from '~/components/core/Atomic/Tooltip';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SortableTable from '~/components/core/Tables/SortableTable';
import Text from '~/components/core/TextComponents/Text';
import Checkbox from '~/components/ui/atoms/Checkbox';

import type { SubOrgPreviewRow } from '../types';

interface SubOrgSelectionStepProps {
  previewRows: SubOrgPreviewRow[];
  selectedSubOrgIds: Set<number>;
  onSelectionChange: (selectedIds: Set<number>) => void;
  isLoading: boolean;
  isStarting: boolean;
}

const SubOrgSelectionStep: React.FC<SubOrgSelectionStepProps> = ({
  previewRows,
  selectedSubOrgIds,
  onSelectionChange,
  isLoading,
  isStarting,
}) => {
  const aiEnabledRows = previewRows.filter((row) => row.is_ai_enabled);
  const allAiEnabledSelected =
    aiEnabledRows.length > 0 && aiEnabledRows.every((row) => selectedSubOrgIds.has(row.sub_organization_id!));
  const someSelected = aiEnabledRows.some((row) => selectedSubOrgIds.has(row.sub_organization_id!));

  const handleSelectAll = () => {
    if (allAiEnabledSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(aiEnabledRows.map((row) => row.sub_organization_id!)));
    }
  };

  const handleRowToggle = (subOrgId: number) => {
    const next = new Set(selectedSubOrgIds);
    if (next.has(subOrgId)) {
      next.delete(subOrgId);
    } else {
      next.add(subOrgId);
    }
    onSelectionChange(next);
  };

  const columns = [
    {
      id: 'checkbox',
      label: '',
      width: '48px',
      disableSort: true,
      specialHeader: () => (
        <Checkbox
          checked={allAiEnabledSelected}
          indeterminate={someSelected && !allAiEnabledSelected}
          onChange={handleSelectAll}
          disabled={aiEnabledRows.length === 0}
        />
      ),
      specialCell: (row: SubOrgPreviewRow) => {
        if (!row.is_ai_enabled) {
          return (
            <Tooltip title="AI is not enabled for this sub-organization">
              <span>
                <Checkbox checked={false} disabled />
              </span>
            </Tooltip>
          );
        }
        return (
          <Checkbox
            checked={selectedSubOrgIds.has(row.sub_organization_id!)}
            onChange={() => handleRowToggle(row.sub_organization_id!)}
          />
        );
      },
    },
    { id: 'name', label: 'Sub-Org Name' },
    { id: 'external_id', label: 'External ID' },
    { id: 'claims_count', label: 'Claims' },
    { id: 'documents_count', label: 'Documents' },
    { id: 'communications_count', label: 'Communications' },
    { id: 'notes_count', label: 'Notes' },
    {
      id: 'predicted_cost',
      label: 'Predicted Cost',
      specialCell: (row: SubOrgPreviewRow) =>
        row.predicted_cost !== null && row.predicted_cost !== undefined ? `$${row.predicted_cost.toFixed(2)}` : '-',
    },
    {
      id: 'is_ai_enabled',
      label: 'AI Enabled',
      specialCell: (row: SubOrgPreviewRow) => (
        <Text
          variant={Text.VARIANTS.XS}
          colorVariant={row.is_ai_enabled ? Text.COLOR_VARIANTS.PRIMARY : Text.COLOR_VARIANTS.ERROR}
        >
          {row.is_ai_enabled ? 'Yes' : 'No'}
        </Text>
      ),
    },
  ];

  const rows = previewRows.map((row) => ({
    ...row,
    id: row.sub_organization_id!,
  }));

  const greyedOutRowsIds = previewRows.filter((row) => !row.is_ai_enabled).map((row) => row.sub_organization_id!);

  return (
    <LoadingSwitch isLoading={isLoading || isStarting} isError={false}>
      <div className="w-full">
        <SortableTable columns={columns} rows={rows} stickyHeader greyedOutRowsIds={greyedOutRowsIds} />
      </div>
    </LoadingSwitch>
  );
};

export default SubOrgSelectionStep;
